import { FC } from 'react';
import { User } from 'lucide-react';
import { cn } from '../../../../utils/cn';
import type { EditableProfileData } from './types';

export interface PersonalInfoFormProps {
  formData: EditableProfileData;
  onInputChange: (field: keyof EditableProfileData, value: string | string[]) => void;
}

export const PersonalInfoForm: FC<PersonalInfoFormProps> = ({
  formData,
  onInputChange,
}) => {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-4 flex items-center gap-2">
        <User className="w-5 h-5" />
        Personal Information
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label
            htmlFor="firstName"
            className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1"
          >
            First Name *
          </label>
          <input
            id="firstName"
            type="text"
            value={formData.firstName}
            onChange={(e) => onInputChange('firstName', e.target.value)}
            className={cn(
              'w-full px-3 py-2 text-sm rounded-md border',
              'bg-white dark:bg-slate-700',
              'text-gray-900 dark:text-slate-100',
              'border-gray-300 dark:border-slate-600',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            )}
          />
        </div>
        <div>
          <label
            htmlFor="lastName"
            className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1"
          >
            Last Name *
          </label>
          <input
            id="lastName"
            type="text"
            value={formData.lastName}
            onChange={(e) => onInputChange('lastName', e.target.value)}
            className={cn(
              'w-full px-3 py-2 text-sm rounded-md border',
              'bg-white dark:bg-slate-700',
              'text-gray-900 dark:text-slate-100',
              'border-gray-300 dark:border-slate-600',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            )}
          />
        </div>
        <div className="sm:col-span-2">
          <label
            htmlFor="phone"
            className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1"
          >
            Phone Number
          </label>
          <input
            id="phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => onInputChange('phone', e.target.value)}
            placeholder="+1 (555) 123-4567"
            className={cn(
              'w-full px-3 py-2 text-sm rounded-md border',
              'bg-white dark:bg-slate-700',
              'text-gray-900 dark:text-slate-100',
              'border-gray-300 dark:border-slate-600',
              'placeholder-gray-400 dark:placeholder-slate-500',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            )}
          />
        </div>
        <div className="sm:col-span-2">
          <label
            htmlFor="bio"
            className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1"
          >
            Bio
          </label>
          <textarea
            id="bio"
            value={formData.bio}
            onChange={(e) => onInputChange('bio', e.target.value)}
            rows={4}
            placeholder="Tell clients about yourself, your experience, and your approach to massage therapy..."
            className={cn(
              'w-full px-3 py-2 text-sm rounded-md border resize-none',
              'bg-white dark:bg-slate-700',
              'text-gray-900 dark:text-slate-100',
              'border-gray-300 dark:border-slate-600',
              'placeholder-gray-400 dark:placeholder-slate-500',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            )}
          />
          <p className="text-xs text-gray-500 dark:text-slate-500 mt-1">
            {formData.bio.length}/1000 characters
          </p>
        </div>
      </div>
    </div>
  );
};
